import React, {useState} from 'react';
import {Box, Typography, Grid, Tooltip, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, StatCard, BandChip, GuidanceBanner, secondaryMarkerSx, MARKER_SECONDARY, readinessBandColor} from '../components/widgets.comp';
import {READINESS_BAND_COLORS} from '../../../utils/constants';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, benchmarkOf, readinessAreaNumber} from '../networkData';
import {isSuppressedItem, isSuppressedKpi, kpiNote} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems} from '../../DashboardCommon/barSort';

import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
/* Slide 15 — Child Online Safety Readiness Overview (SND-15-01 … 03).

     SND-15-01  Network Average       SND.S_4.C_2   KPICard
     SND-15-02  Global Average        SND.S_4.C_4   KPICard
     SND-15-03  Readiness Area Scores SND.S_3.C_6   BarChartBMCard (8 areas, Global avg.)

   Only the Global avg marker is drawn, consistent with the Executive Overview.
   The deck's "Replace this with rubric band colored chips." note is applied:
   the legend is the full High/Moderate/Basic/Low chip row. */
const Legend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
            {Object.keys(READINESS_BAND_COLORS).map((band) => (
                <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]}/>
            ))}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
        </Box>
    </Box>
);

// An area under the n floor (suppressed) shows the placeholder in place of its bar, score and band.
const AreaBar = ({area, name, score, band, global, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
        <Box sx={{width: {xs: 130, md: 180}, flexShrink: 0}}>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{area}</Typography>
            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY}}>{name}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <Box sx={{flex: 1, position: 'relative', height: 16, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '8px'}}>
            <Tooltip title={fmtValue(score)} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${score === null ? 0 : score}%`, backgroundColor: readinessBandColor(band), borderRadius: '8px', cursor: 'pointer'}}/>
            </Tooltip>
            {global != null && (
                <Tooltip title={benchmarkText('Global avg', global)} arrow placement="bottom">
                    <Box sx={secondaryMarkerSx(global)}/>
                </Tooltip>
            )}
        </Box>
        <Typography sx={{fontSize: '16px', fontWeight: 800, color: NAVY, width: 34, textAlign: 'right', flexShrink: 0}}>{fmtValue(score)}</Typography>
        <Box sx={{flexShrink: 0}}>{band && <BandChip label={band} color={readinessBandColor(band)}/>}</Box>
        </>)}
    </Box>
);

const READINESS_SUMMARY_SECTION_CODE = 'S_3';
const READINESS_OVERVIEW_SECTION_CODE = 'S_4';

const CARD_SECTIONS = [
    READINESS_SUMMARY_SECTION_CODE,
    READINESS_OVERVIEW_SECTION_CODE,
];

const ReadinessOverviewTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }

    const networkAvg = findCard(dashboardData, READINESS_OVERVIEW_SECTION_CODE, 'C_2');
    const globalAvg = findCard(dashboardData, READINESS_OVERVIEW_SECTION_CODE, 'C_4');

    // All eight areas, put back in framework order — the card ranks them by score.
    const READINESS_AREAS = (findCard(dashboardData, READINESS_SUMMARY_SECTION_CODE, 'C_6')?.items || [])
        .map((item) => {
            const num = readinessAreaNumber(item);
            const suppressed = isSuppressedItem(item);
            return {
                num,
                area: num ? `Readiness Area ${num}` : '',
                name: item.label,
                suppressed,
                score: suppressed ? null : whole(item.value),
                band: suppressed ? null : item.band,
                global: benchmarkOf(item, 'Global avg.'),
            };
        })
        .sort((a, b) => (a.num || 99) - (b.num || 99));
    // Group order is the framework order above; the value modes rank the eight areas (suppressed ones last).
    const shownAreas = sortItems(READINESS_AREAS, sortMode);

    // The lowest area is picked among the areas that show a score.
    const scoredAreas = READINESS_AREAS.filter((a) => a.score !== null);
    const lowestArea = scoredAreas.length
        ? scoredAreas.reduce((min, a) => (a.score < min.score ? a : min), scoredAreas[0])
        : null;
    // The comparison clause only when a global figure exists for that area.
    const guidanceText = lowestArea
        ? `${lowestArea.area} · ${lowestArea.name} is the lowest network target (${lowestArea.score})${lowestArea.global !== null ? `, ${lowestArea.score < lowestArea.global ? 'below' : 'against'} the global average (${lowestArea.global})` : ''}, and is the first focus. If a gap shows network-wide, use a whole-network Area; if it is concentrated, use the by-school view before choosing where to act.`
        : null;

    return (
        <Box>
            <PageHeader
                title="Child Online Safety Readiness Overview"
                subtitle="Which child online safety readiness areas need attention across the network?"
            />

            <Grid container spacing={2.5} sx={{mb: 3}}>
                <Grid item xs={12} md={6}>
                    {networkAvg && (isSuppressedKpi(networkAvg)
                        ? <StatCard label="Network Average" value="—" sub={kpiNote(networkAvg)}/>
                        : <StatCard label="Network Average" value={String(whole(networkAvg.kpi_value) ?? '—')} band={networkAvg.kpi_band} bandColorOverride={readinessBandColor(networkAvg.kpi_band)}/>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    {globalAvg && (
                        <StatCard label="Global Average" value={String(whole(globalAvg.kpi_value) ?? '—')} band={globalAvg.kpi_band} bandColorOverride={readinessBandColor(globalAvg.kpi_band)}/>
                    )}
                </Grid>
            </Grid>

            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                        Readiness Area Scores
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <Legend/>
                        <SortToggle value={sortMode} onChange={setSortMode}/>
                    </Box>
                </Box>
                {shownAreas.map((a) => <AreaBar key={a.name} {...a}/>)}
            </Box>

            {guidanceText && <GuidanceBanner text={guidanceText}/>}
        </Box>
    );
};

export default ReadinessOverviewTab;
